import { FC, memo, type ReactNode } from 'react';
import cn from 'classnames';
import { Select } from 'antd';
import { TaskResult } from '../../../../models/enums/TaskResult';
import { AttemptStateOptions } from '../../../../models/enums/AttemptState';
import { Clinic } from '../../../../models/MedVox/Clinic';
import { OptionLike } from '../../../../hooks/shared/useFilterOption';
import { useFilterOptions } from '../../../../hooks/shared/useFilterOptions';
import { JournalDictionaryOption } from '../../../../hooks/shared/useJournalQueryState';
import { FILTER_FIELD_TEXTS } from '../../../../components/shared/filterFieldTexts';
import { ClinicSelect } from '../../../../components/shared/ClinicSelect';
import filterDefaults from '../../../../components/shared/filterPanelDefaults.module.css';

interface Props {
  campaignFilter?: ReactNode;
  clinics: Clinic[];
  filterOption: (
    inputValue: string,
    option?: OptionLike | undefined,
  ) => boolean;
  clinicValue: string[];
  attemptStatusValue: string[];
  resultValue: string[];
  scenarioValue: string[];
  onClinicsChange: (value: string[]) => void;
  onAttemptStatusChange: (value: string[]) => void;
  onResultChange: (value: string[]) => void;
  onScenarioChange: (value: string[]) => void;
  onOpenClinics: (value: boolean) => void;
  onOpenAttemptStatus: (value: boolean) => void;
  onOpenResult: (value: boolean) => void;
  onOpenScenario: (value: boolean) => void;
  resultOptions?: JournalDictionaryOption[];
  scenarioOptions?: JournalDictionaryOption[];
  disabled?: boolean;
}

export const IncomingCallsFilter: FC<Props> = memo(
  ({
    campaignFilter,
    clinics,
    filterOption,
    clinicValue,
    attemptStatusValue,
    resultValue,
    scenarioValue,
    onClinicsChange,
    onAttemptStatusChange,
    onResultChange,
    onScenarioChange,
    onOpenClinics,
    onOpenAttemptStatus,
    onOpenResult,
    onOpenScenario,
    resultOptions = [],
    scenarioOptions = [],
    disabled = false,
  }: Props) => {
    const resultSource =
      resultOptions.length > 0
        ? resultOptions
        : TaskResult.map((result) => ({
            value: result.value,
            id: result.value,
            label: result.text,
          }));

    const scenarioArray = useFilterOptions(
      scenarioOptions,
      scenarioValue,
      (s) => scenarioValue.includes(s.value),
      (s) => ({ value: s.value, id: s.value, label: s.label }),
    );

    const attemptStatusArray = useFilterOptions(
      AttemptStateOptions,
      attemptStatusValue,
      (s) => attemptStatusValue.includes(s.value),
      (s) => ({ value: s.value, id: s.value, label: s.text }),
    );

    const resultArray = useFilterOptions(
      resultSource,
      resultValue,
      (r) => resultValue.includes(r.value),
      (r) => r,
    );

    return (
      <>
        <div className={filterDefaults.filterBlockColumn}>
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.notificationType.label}
            </label>
            <Select
              allowClear
              className={filterDefaults.filterInput}
              placeholder={FILTER_FIELD_TEXTS.notificationType.placeholder}
              mode="multiple"
              value={scenarioValue}
              filterOption={filterOption}
              onChange={onScenarioChange}
              showSearch={true}
              onOpenChange={onOpenScenario}
              removeIcon={null}
              maxTagCount="responsive"
              maxTagTextLength={24}
              options={scenarioArray}
              autoClearSearchValue={false}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            ></Select>
          </div>
          {campaignFilter}
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.clinic.label}
            </label>
            <ClinicSelect
              clinics={clinics}
              value={clinicValue}
              onChange={onClinicsChange}
              className={filterDefaults.filterInput}
              placeholder={FILTER_FIELD_TEXTS.clinic.placeholder}
              filterOption={filterOption}
              onOpenChange={onOpenClinics}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            />
          </div>
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>Статус звонка</label>
            <Select
              allowClear
              className={filterDefaults.filterInput}
              placeholder="Выберите статус звонка"
              mode="multiple"
              value={attemptStatusValue}
              filterOption={filterOption}
              onChange={onAttemptStatusChange}
              showSearch={true}
              onOpenChange={onOpenAttemptStatus}
              removeIcon={null}
              maxTagCount="responsive"
              maxTagTextLength={24}
              options={attemptStatusArray}
              autoClearSearchValue={false}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            ></Select>
          </div>
          <div className={filterDefaults.filterInputsContainer}>
            <label className={filterDefaults.filterLabel}>
              {FILTER_FIELD_TEXTS.taskResult.label}
            </label>
            <Select
              allowClear
              className={filterDefaults.filterInput}
              placeholder={FILTER_FIELD_TEXTS.taskResult.placeholder}
              mode="multiple"
              value={resultValue}
              filterOption={filterOption}
              onChange={onResultChange}
              showSearch={true}
              onOpenChange={onOpenResult}
              removeIcon={null}
              maxTagCount="responsive"
              maxTagTextLength={24}
              options={resultArray}
              autoClearSearchValue={false}
              disabled={disabled}
              optionRender={(state) => (
                <span className={filterDefaults.filterOptionText}>
                  {' '}
                  {state.label}
                </span>
              )}
            ></Select>
          </div>
        </div>
      </>
    );
  },
);

IncomingCallsFilter.displayName = 'IncomingCallsFilter';
